// Copyright (c) 2026, IPivot. All rights reserved.
'use strict';

import { useContext, useEffect, useState } from 'react';
import {
    Alert, Button, Col, Container, Form, ProgressBar, Row, Spinner
} from 'react-bootstrap';
import { getDiff, revertChange, revertAll, exportDiff, DiffChange } from '../../data';
import { MetadataContext } from '../MetadataContext';
import { StatusContext } from '../StatusContext';
import { WsContext } from '../WsContext';
import { formatBytes } from '../../utils/formatBytes';
import MergeDiffDialog from '../MergeDiffDialog';
import { PendingChange } from '../../data';
import DiffHoverPanel from '../DiffHoverPanel';
import DiffChangesTable, { changeKey } from '../DiffChangesTable';

function buildStamp(now: Date) {
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getFullYear() % 100)}_${pad(now.getHours())}${pad(now.getMinutes())}`;
}

// ── Page ───────────────────────────────────────────────────────────────────

function ShowChangesPage() {
    const metadataContext = useContext(MetadataContext);
    const statusContext = useContext(StatusContext);
    const wsContext = useContext(WsContext);

    const [loading, setLoading] = useState(true);
    const [changes, setChanges] = useState<DiffChange[]>([]);
    const [hasLastOpened, setHasLastOpened] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [reverting, setReverting] = useState<string | null>(null);
    const [revertingAll, setRevertingAll] = useState(false);
    // click → modal
    const [diffItem, setDiffItem] = useState<PendingChange | null>(null);
    // hover → side panel (pointer-events:none)
    const [hoverDiff, setHoverDiff] = useState<PendingChange | null>(null);

    // Submit fields
    const [submitCloud, setSubmitCloud] = useState(true);
    const [submitFilename, setSubmitFilename] = useState('');
    const [submitNotes, setSubmitNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitProgressId, setSubmitProgressId] = useState<string | null>(null);
    const [submitProgressNow, setSubmitProgressNow] = useState(0);
    const [submitProgressLabel, setSubmitProgressLabel] = useState('');
    const [submitBytesTransferred, setSubmitBytesTransferred] = useState(0);
    const [submitTotalBytes, setSubmitTotalBytes] = useState<number | null>(null);

    function resolveUsername() {
        const raw = String((metadataContext as any)?.user_dir || '').trim();
        return (!raw || raw === 'unknown' || raw === 'loading') ? 'user' : raw;
    }

    async function loadDiff() {
        setLoading(true);
        setLoadError(null);
        try {
            const res = await getDiff();
            setHasLastOpened(res.hasLastOpened);
            setChanges(res.changes);
        } catch (e: any) {
            setLoadError(e.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        const now = new Date();
        const username = resolveUsername();
        setSubmitNotes(`submitted by ${username} ${now.toLocaleString()}`);
        setSubmitFilename(`${username}_${buildStamp(now)}`);
        loadDiff();
    }, []);

    useEffect(() => {
        if (!wsContext.ws) return;
        const handler = (msg: any) => {
            if (!submitProgressId || msg?.progressId !== submitProgressId || msg?.op !== 'save') return;
            if (typeof msg?.now === 'number') setSubmitProgressNow(Math.max(0, Math.min(100, msg.now)));
            if (typeof msg?.bytesTransferred === 'number') setSubmitBytesTransferred(Math.max(0, msg.bytesTransferred));
            if (typeof msg?.totalBytes === 'number' && msg.totalBytes > 0) setSubmitTotalBytes(msg.totalBytes);
            setSubmitProgressLabel(msg?.label || 'Submitting...');
        };
        wsContext.ws.on('fileTransferProgress', handler);
        return () => { wsContext.ws.off('fileTransferProgress', handler); };
    }, [wsContext.ws, submitProgressId]);

    async function handleRevert(c: DiffChange) {
        const key = changeKey(c);
        const id = `${c.type}:${key}`;
        setReverting(id);
        try {
            await revertChange(c.type, key, c.op);
            await loadDiff();
        } catch (e: any) {
            statusContext.postError(`Revert failed: ${e.message}`);
        } finally {
            setReverting(null);
        }
    }

    async function handleRevertAll() {
        setRevertingAll(true);
        try {
            await revertAll();
            await loadDiff();
            statusContext.postInfo('Reverted all changes — database restored to last opened state.');
        } catch (e: any) {
            statusContext.postError(`Revert all failed: ${e.message}`);
        } finally {
            setRevertingAll(false);
        }
    }

    async function handleSubmit() {
        const trimmedName = submitFilename.trim();
        if (!trimmedName) { setSubmitError('Filename is required.'); return; }
        if (changes.length === 0) { setSubmitError('No changes to submit.'); return; }
        const progressId = crypto.randomUUID();
        setSubmitProgressId(progressId);
        setSubmitProgressNow(0);
        setSubmitProgressLabel('Preparing submit...');
        setSubmitBytesTransferred(0);
        setSubmitTotalBytes(null);
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const res = await exportDiff(trimmedName, submitCloud, { info: submitNotes.trim(), serial: '0000' }, progressId);
            statusContext.postInfo(`Submitted ${res.filename} to ${submitCloud ? 'cloud' : 'local'} queue (${res.fingerprints} fps, ${res.categories} cats)`);
        } catch (e: any) {
            setSubmitError(e?.message || 'Submit failed');
            statusContext.postError(`Submit failed: ${e?.message || e}`);
        } finally {
            setIsSubmitting(false);
        }
    }

    if (loading) {
        return (
            <Container fluid className="px-4 mt-4">
                <Spinner animation="border" size="sm" className="me-2" /> Loading changes...
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="px-4 mt-4">
                <Alert variant="danger">{loadError}</Alert>
                <Button variant="outline-secondary" onClick={loadDiff}>Retry</Button>
            </Container>
        );
    }

    if (!hasLastOpened) {
        return (
            <Container fluid className="px-4 mt-4">
                <Alert variant="info">
                    No baseline snapshot found. Load a database from cloud or local to establish a baseline, then make changes to see them here.
                </Alert>
            </Container>
        );
    }

    const fpCount = changes.filter(c => c.type === 'fingerprint').length;
    const catCount = changes.filter(c => c.type === 'category').length;

    return (
        <>
        <Container fluid>
            <Row className="px-4">
                <Col>
                    <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                        <h3 className="mb-0">
                            Show Changes
                            {changes.length > 0 && (
                                <span className="ms-2 text-muted fs-6 fw-normal">
                                    {fpCount} fingerprint{fpCount !== 1 ? 's' : ''}{catCount > 0 ? `, ${catCount} categor${catCount !== 1 ? 'ies' : 'y'}` : ''}
                                </span>
                            )}
                        </h3>
                        <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-secondary" onClick={loadDiff} disabled={revertingAll}>Refresh</Button>
                            {changes.length > 0 && (
                                <Button size="sm" variant="outline-danger" onClick={handleRevertAll} disabled={revertingAll}>
                                    {revertingAll ? <><Spinner size="sm" animation="border" className="me-1" />Reverting...</> : 'Revert All'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {changes.length === 0 ? (
                        <div
                            className="mt-3 px-3 py-2 rounded font-monospace"
                            style={{
                                background: '#0d1117',
                                color: '#39ff14',
                                border: '1px solid #39ff1440',
                                fontSize: '0.85rem',
                                letterSpacing: '0.02em',
                            }}
                        >
                            <span style={{ opacity: 0.5 }}>$ </span>diff --stat current lastOpened
                            <br />
                            <span style={{ opacity: 0.7 }}>0 files changed</span>
                            <br />
                            <span style={{ color: '#39ff14' }}>No changes since last opened database.</span>
                        </div>
                    ) : (
                        <DiffChangesTable
                            changes={changes}
                            onOpen={(pending) => { setHoverDiff(null); setDiffItem(pending); }}
                            onHoverChange={setHoverDiff}
                            showRevert={true}
                            revertingKey={reverting}
                            revertingAll={revertingAll}
                            onRevert={handleRevert}
                        />
                    )}

                    {/* Submit controls — horizontal layout */}
                    <div className="mt-3 mb-5">
                        <h5 className="mb-2">Submit to Queue</h5>
                        <Row className="g-2 align-items-end">
                            <Col xs="auto">
                                <Form.Label className="mb-1 small text-muted">Destination</Form.Label>
                                <div className="btn-group d-block" role="group">
                                    <Button size="sm" variant={submitCloud ? 'primary' : 'outline-primary'} onClick={() => setSubmitCloud(true)} disabled={isSubmitting}>Cloud</Button>
                                    <Button size="sm" variant={!submitCloud ? 'primary' : 'outline-primary'} onClick={() => setSubmitCloud(false)} disabled={isSubmitting}>Local</Button>
                                </div>
                            </Col>
                            <Col style={{ minWidth: 180, maxWidth: 260 }}>
                                <Form.Label className="mb-1 small text-muted">Filename</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    value={submitFilename}
                                    onChange={e => setSubmitFilename(e.target.value)}
                                    disabled={isSubmitting}
                                />
                            </Col>
                            <Col>
                                <Form.Label className="mb-1 small text-muted">Notes</Form.Label>
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    value={submitNotes}
                                    onChange={e => setSubmitNotes(e.target.value)}
                                    disabled={isSubmitting}
                                />
                            </Col>
                            <Col xs="auto">
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || changes.length === 0}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    {isSubmitting
                                        ? <><Spinner size="sm" animation="border" className="me-1" />Submitting...</>
                                        : `Submit${changes.length > 0 ? ` (${changes.length})` : ''}`}
                                </Button>
                            </Col>
                        </Row>
                        {submitFilename && (
                            <div className="mt-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                Saved as <span className="font-monospace">library/queue/{submitFilename}.merge</span>
                            </div>
                        )}
                        {submitProgressId && (
                            <div className="mt-2" style={{ maxWidth: 480 }}>
                                <ProgressBar now={submitProgressNow} label={`${submitProgressNow.toFixed(0)}%`} />
                                <div className="mt-1 text-muted small">
                                    {submitProgressLabel || 'Submitting...'}
                                    {submitTotalBytes ? (
                                        <span className="ms-2">{formatBytes(submitBytesTransferred)} / {formatBytes(submitTotalBytes)}</span>
                                    ) : null}
                                </div>
                            </div>
                        )}
                        {submitError && <Alert variant="danger" className="mt-2 mb-0 py-1 px-2 small">{submitError}</Alert>}
                    </div>
                </Col>
            </Row>
        </Container>

        {/* Hover panel — fixed right, pointer-events:none, dismissed on mouseLeave of op cell */}
        <DiffHoverPanel change={hoverDiff} />

        {/* Click → modal */}
        <MergeDiffDialog change={diffItem} onClose={() => setDiffItem(null)} />
        </>
    );
}

export default ShowChangesPage;
